import { AutofillMonitor } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { samlPost } from '../../functions/saml-post';
import {
  LoginDeepLinkParams,
  loginDeepLinkQueryParams,
  navigateAfterLogin,
  resolveLoginNavigateTarget,
} from '../../functions/login-navigate-to';
import { Login } from '../../models/api/core/auth/login';
import { LoginResponse } from '../../models/api/core/auth/login-response';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { ToastComponent } from '../core/labels/toast.component';

@Component({
  selector: 'app-login',
  imports: [
    InputComponent,
    ButtonComponent,
    RouterModule,
    ToastComponent,
    CommonModule,
    ApiErrorLabelComponent,
    ReactiveFormsModule,
    LinkLabelComponent,
  ],
  template: `
    <!-- Hidden live region for errors - must exist on page load for screen readers -->
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      class="tw-sr-only"
      [attr.id]="'login-error-live-region'"
    >
      {{
        (status === 'Failed' || status === 'ClientNotWhitelisted') && message
          ? message
          : ''
      }}
    </div>
    <form
      class="tw-px-4 tw-pt-4 tw-pb-2 tw-min-w-0 tw-max-w-full"
      [formGroup]="fg"
      (ngSubmit)="login()"
    >
      <div class="tw-flex tw-justify-center tw-flex-col tw-pt-2 tw-pb-2">
        <div
          class="tw-flex tw-justify-center tw-pb-6"
          [class.tw-hidden]="!fromSessionExpired"
          [attr.aria-hidden]="!fromSessionExpired ? 'true' : null"
        >
          <app-toast>Your session has expired.</app-toast>
        </div>
        <div
          class="tw-flex tw-justify-center tw-pb-6"
          [class.tw-hidden]="
            status !== 'Failed' && status !== 'ClientNotWhitelisted'
          "
          [attr.aria-hidden]="
            status !== 'Failed' && status !== 'ClientNotWhitelisted'
              ? 'true'
              : null
          "
        >
          <app-api-error-label [error]="message" />
        </div>
        <div
          class="tw-flex tw-justify-center tw-pb-6"
          [class.tw-hidden]="!passwordReset"
          [attr.aria-hidden]="!passwordReset ? 'true' : null"
        >
          <app-toast>Your password has been changed.</app-toast>
        </div>
        <div
          class="tw-flex tw-justify-center tw-pb-6"
          [class.tw-hidden]="!passwordResetExpired"
          [attr.aria-hidden]="!passwordResetExpired ? 'true' : null"
        >
          <app-toast type="Error"
            >The password reset session has expired.</app-toast
          >
        </div>
        <div
          class="tw-flex tw-justify-center tw-pb-6"
          [class.tw-hidden]="!mfaExpired"
          [attr.aria-hidden]="!mfaExpired ? 'true' : null"
        >
          <app-toast type="Error"
            >The multifactor authentication session has expired. Please login
            again.</app-toast
          >
        </div>
        <div class="tw-flex tw-flex-col">
          <div class="tw-flex tw-flex-full tw-justify-center tw-max-w-[340px]">
            <app-input
              [control]="fg.controls.userName"
              label="Username"
              #username
              icon="fa-light fa-user"
              [large]="true"
              [maxlength]="8"
              [classes]="'tw-min-w-[310px]'"
              [showMaxlength]="false"
              [showRequiredIndicator]="false"
            ></app-input>
          </div>
          <div class="tw-flex tw-justify-end tw-pb-2 tw-px-2 tw-max-w-[320px]">
            <app-link-label routerLink="/login/forgot-username"
              >Forgot your username?</app-link-label
            >
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-pt-6">
          <div class="tw-flex tw-flex-full tw-justify-center tw-max-w-[340px]">
            <app-input
              [control]="fg.controls.password"
              label="Password"
              #password
              icon="fa-light fa-lock"
              [large]="true"
              type="Password"
              [showReveal]="true"
              [showRequiredIndicator]="false"
              [classes]="'tw-min-w-[310px]'"
            ></app-input>
          </div>
          <div class="tw-flex tw-justify-end tw-px-2 tw-pb-4 tw-max-w-[320px]">
            <app-link-label routerLink="/login/forgot-password"
              >Forgot your password?</app-link-label
            >
          </div>
        </div>
        <div
          class="tw-pt-8 tw-flex tw-flex-full tw-justify-center tw-max-w-[340px]"
        >
          <app-button
            type="submit"
            center1
            label="Log in"
            [loading]="loading"
            [disabled]="!(fg.valid || autofillDetected)"
            [classes]="
              'tw-text-[18px] tw-h-[40px] tw-min-w-[300px] tw-w-full sm:tw-w-auto'
            "
          />
        </div>
      </div>
    </form>
  `,
  styles: ``,
})
export class LoginComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private route = inject(ActivatedRoute);
  private autofillMonitor = inject(AutofillMonitor);
  private cookieService = inject(CookieService);

  @ViewChild('username')
  usernameInput: InputComponent;

  @ViewChild('password')
  passwordInput: InputComponent;

  loginVals: Login;
  results: LoginResponse;
  status: string;
  message: string;
  loading = false;
  autofillDetected = false;
  fromSessionExpired = false;
  passwordReset = false;
  mfaExpired = false;
  passwordResetExpired = false;
  reportCode: string | null = null;
  navigateTo: string | null = null;
  recordSource: string | null = null;
  recordId: string | null = null;
  fg = new FormGroup({
    userName: new FormControl<string>(null, Validators.required),
    password: new FormControl<string>(null, Validators.required),
  });
  ngAfterViewInit(): void {
    this.dispose.subs = this.autofillMonitor
      .monitor(this.usernameInput.inputElement)
      .subscribe((event) => {
        this.autofillDetected = event.isAutofilled;
      });

    this.dispose.subs = this.autofillMonitor
      .monitor(this.passwordInput.inputElement)
      .subscribe((event) => {
        this.autofillDetected = event.isAutofilled;
      });
  }

  async ngOnInit() {
    this.dispose.subs = this.route.queryParamMap.subscribe((params) => {
      this.applyDeepLinkParams(params);
    });
  }

  private applyDeepLinkParams(params: ParamMap): void {
    this.fromSessionExpired = params.get('sessionExpired') === 'true';
    this.passwordReset = params.get('passwordReset') === 'true';
    this.passwordResetExpired = params.get('passwordResetExpired') === 'true';
    this.mfaExpired = params.get('mfaExpired') === 'true';
    this.reportCode = params.get('reportcode');
    this.navigateTo = params.get('navigateto');
    this.recordSource = params.get('recordSource');
    this.recordId = params.get('recordId');
  }

  /*   checkUserNameInvalid(): boolean {
    if (this.fg.controls.userName.valueChanges) {
      const c = this.fg.controls.userName;
      return c.invalid && (c.dirty || c.touched);
    }
    return false;
  }

  checkPasswordInvalid(): boolean {
    if (this.fg.controls.password.valueChanges) {
      const c = this.fg.controls.password;
      return c.invalid && (c.dirty || c.touched);
    }
    return false;
  } */

  private deepLinkParams(): LoginDeepLinkParams {
    return {
      navigateTo: this.navigateTo,
      reportCode: this.reportCode,
      recordSource: this.recordSource,
      recordId: this.recordId,
    };
  }

  async login() {
    this.message = null;
    this.loading = true;
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.loginVals = {
      userName: this.fg.value.userName,
      password: this.fg.value.password,
    };

    // check status of login (Success, Failed, Locked, and Expired)
    try {
      await this.api.auth.getXsrf();
      this.cookieService.delete('Scms.LogoutUrl');
      this.results = await this.api.auth.login(this.loginVals);
      await this.api.auth.getXsrf();
      if (this.results.result === 'Success') {
        this.session.clearCache();
        this.session.user = await this.api.user.get();
        this.session.updateUser.emit(true);
        const target = resolveLoginNavigateTarget(this.deepLinkParams());
        navigateAfterLogin(this.router, target);
        this.session.resetSessionActivity();
      } else if (this.results.result === 'ClassicSsoRedirect') {
        const fieldName = this.results.samlResponseFieldName ?? 'SAMLResponse';
        samlPost(
          this.results.samlResponse,
          this.results.targetUrl,
          fieldName,
          this.results.relayState,
          '_self',
        );
      } else if (this.results.result === 'Failed') {
        this.message =
          'We could not validate your information. Make sure your username ' +
          'and password are correct and try again.';
        this.fg.reset();
        this.autofillDetected = false;
      } else if (this.results.result === 'Expired') {
        this.router.navigate(['/login/password-reset'], {
          state: {
            pwReset: this.results,
          },
        });
      } else if (this.results.result === 'Locked') {
        this.router.navigate(['/login/account-locked']);
      } else if (this.results.result === 'MfaRequired') {
        this.router.navigate(['/login/mfa'], {
          queryParams: loginDeepLinkQueryParams(this.deepLinkParams()),
          queryParamsHandling: 'merge',
          state: {
            mfa: this.results,
            navigateTo: this.navigateTo,
            recordSource: this.recordSource,
            recordId: this.recordId,
          },
        });
      }
      this.status = this.results.result;
    } catch {
      this.status = 'Failed';
      this.message = 'An error has occurred.';
    } finally {
      this.loading = false;
    }
  }
}
